import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';

import { View } from '../Base';
import palette from '../styles/palette';
import Button from '../Button';
import useLoader from '../../hooks/useLoader';
import {
  onAcceptRequest,
  onDeferRequest,
  onRejectRequest,
} from '../../store/actions/request';
import {
  ACCEPT_DRIVER_REQUEST_PROCESS,
  REJECT_DRIVER_REQUEST_PROCESS,
} from '../../enums/loaderProcess';
import {
  getAffectedRequests,
  getPlannedRequestsForReject,
  getRemainingConsignmentRequests,
  getRemainingToteRequests,
  isAddParcelRequest,
  isRequestFromRoutePlanner,
} from '../../models/request';
import { wrapPromise } from '../../utils/promise';
import modals from '../../enums/modals';
import { ACCEPT, DEFER, REJECT, TRANSFER } from '../../constants/strings';
import {
  isRemoveCollectionRequest,
  isRemoveParcelRequest,
} from '../../models/routeDetail';
import { getRoutePlannerAddParcelRequests } from '../../store/selectors/route';
import {
  allowConsignmentSplitting,
  allowToteTransfers,
} from '../../store/selectors/app';
import { useModalContext } from '../ModalControlled/ModalProvider';

const useStyles = makeStyles({
  actionsContainer: {
    marginTop: '10px',
  },
  actionButton: {
    fontSize: '12px',
    fontWeight: '500',
    lineHeight: '16px',
    padding: '0',
    height: 'auto',
    minWidth: 'auto',
    marginRight: '10px',
    color: palette.sunburst,
  },
});

const PopupRequestActions = ({ stop, onClose }) => {
  const styles = useStyles();
  const dispatch = useDispatch();
  const { requests } = stop;
  const [showLoader, hideLoader] = useLoader();
  const { getModalRef } = useModalContext();
  const routeAddRequests = useSelector(getRoutePlannerAddParcelRequests);
  const allowDepotConsignmentSplitting = useSelector(allowConsignmentSplitting);
  const allowDepotToteTransfers = useSelector(allowToteTransfers);

  const affectedRequests = useMemo(() => {
    const conditions = [];
    if (!allowDepotConsignmentSplitting) {
      conditions.push(getRemainingConsignmentRequests);
    }
    if (!allowDepotToteTransfers) {
      conditions.push(getRemainingToteRequests);
    }
    return getAffectedRequests(routeAddRequests, requests, conditions);
  }, [
    allowDepotConsignmentSplitting,
    allowDepotToteTransfers,
    requests,
    routeAddRequests,
  ]);

  const forbiddenProcessRequest = useMemo(
    () =>
      isRequestFromRoutePlanner(requests[0]) &&
      isAddParcelRequest(requests[0]) &&
      affectedRequests.length !== requests.length,
    [affectedRequests, requests]
  );

  const showRequestModal = useCallback(
    (activeScreen) => {
      const modalRef = getModalRef(modals.REQUEST);
      wrapPromise(
        modalRef.current.showModal({
          stop,
          activeScreen,
        })
      );
    },
    [stop]
  );

  const onDefer = useCallback(() => {
    dispatch(onDeferRequest(stop.requests));
    onClose();
  }, [onClose, stop, dispatch]);

  const onReject = useCallback(async () => {
    const { requests } = stop;
    try {
      showLoader(REJECT_DRIVER_REQUEST_PROCESS);
      if (forbiddenProcessRequest) {
        return showRequestModal();
      }
      const requestsForReject = getPlannedRequestsForReject(requests);
      if (requestsForReject.length) {
        await dispatch(onRejectRequest(requestsForReject));
      }

      if (requestsForReject.length !== requests.length) {
        showRequestModal(TRANSFER);
      }
    } finally {
      hideLoader(REJECT_DRIVER_REQUEST_PROCESS);
      onClose();
    }
  }, [
    stop,
    showLoader,
    forbiddenProcessRequest,
    showRequestModal,
    dispatch,
    hideLoader,
    onClose,
  ]);

  const onAccept = useCallback(async () => {
    const { requests } = stop;
    try {
      showLoader(ACCEPT_DRIVER_REQUEST_PROCESS);
      if (forbiddenProcessRequest) {
        return showRequestModal();
      }
      if (
        isRemoveParcelRequest(requests[0]) ||
        isRemoveCollectionRequest(requests[0])
      ) {
        showRequestModal(TRANSFER);
      } else {
        await dispatch(onAcceptRequest(stop));
      }
    } finally {
      hideLoader(ACCEPT_DRIVER_REQUEST_PROCESS);
      onClose();
    }
  }, [
    stop,
    showLoader,
    forbiddenProcessRequest,
    showRequestModal,
    dispatch,
    hideLoader,
    onClose,
  ]);

  return (
    <View className={styles.actionsContainer}>
      <Button variant="text" className={styles.actionButton} onClick={onAccept}>
        {ACCEPT}
      </Button>
      <Button variant="text" className={styles.actionButton} onClick={onReject}>
        {REJECT}
      </Button>
      <Button variant="text" className={styles.actionButton} onClick={onDefer}>
        {DEFER}
      </Button>
    </View>
  );
};

export default memo(PopupRequestActions);
